import React, { useEffect, useState, useCallback } from 'react';
import API from '../services/api';
import LifecycleModal, { fmtLifecycleDate } from '../components/LifecycleModal';

/* ── Inline status pill ─────────────────────────────────────── */
const STATUS_STYLES = {
  completed:  { bg: '#ECFDF5', color: '#065F46' },
  processing: { bg: '#FFF7ED', color: '#C2410C' },
  failed:     { bg: '#FEF2F2', color: '#991B1B' },
  pending:    { bg: '#F5F3FF', color: '#5B21B6' },
};
function StatusPill({ status }) {
  const st = STATUS_STYLES[status] || { bg: '#F3F4F6', color: '#374151' };
  return (
    <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: st.bg, color: st.color }}>
      {status}
    </span>
  );
}

/* ── Approval pill ──────────────────────────────────────────── */
function ApprovalPill({ status }) {
  if (!status || status === 'pending') return null;
  const st =
    status === 'approved'      ? { bg: '#ECFDF5', color: '#065F46' } :
    status === 'self_approved' ? { bg: '#F0FDF4', color: '#166534' } :
    status === 'rejected'      ? { bg: '#FEF2F2', color: '#991B1B' } :
    status === 'pending_review'? { bg: '#FFF7ED', color: '#92400E' } :
                                 { bg: '#F3F4F6', color: '#374151' };
  return (
    <span style={{ padding: '2px 7px', borderRadius: 999, fontSize: 10, fontWeight: 600, background: st.bg, color: st.color }}>
      {status.replace('_', ' ')}
    </span>
  );
}

/* ── Duration formatter ─────────────────────────────────────── */
function fmtDuration(seconds) {
  if (seconds == null || seconds < 0) return '—';
  if (seconds < 60) return `${seconds} sec`;
  const m = Math.floor(seconds / 60), s = seconds % 60;
  return s > 0 ? `${m} min ${s} sec` : `${m} min`;
}

/* ── Stage colour map ───────────────────────────────────────── */
const STAGE_COLORS = {
  uploaded:        { bg: '#EFF6FF', color: '#1D4ED8', dot: '#3B82F6' },
  queued:          { bg: '#F5F3FF', color: '#6D28D9', dot: '#7C3AED' },
  processing:      { bg: '#FFF7ED', color: '#C2410C', dot: '#F97316' },
  completed:       { bg: '#ECFDF5', color: '#065F46', dot: '#10B981' },
  failed:          { bg: '#FEF2F2', color: '#991B1B', dot: '#EF4444' },
  shared:          { bg: '#EFF6FF', color: '#1E40AF', dot: '#2563EB' },
  re_shared:       { bg: '#FFF7ED', color: '#92400E', dot: '#D97706' },
  self_approved:   { bg: '#ECFDF5', color: '#065F46', dot: '#059669' },
  reviewer_opened: { bg: '#F0FDF4', color: '#14532D', dot: '#22C55E' },
  approved:        { bg: '#ECFDF5', color: '#065F46', dot: '#10B981' },
  rejected:        { bg: '#FEF2F2', color: '#991B1B', dot: '#EF4444' },
  plan_edited:     { bg: '#F5F3FF', color: '#6D28D9', dot: '#8B5CF6' },
  review_deleted:  { bg: '#FEF2F2', color: '#991B1B', dot: '#DC2626' },
};
const stageStyle = stage => STAGE_COLORS[stage] || { bg: '#F9FAFB', color: '#374151', dot: '#9CA3AF' };

/* Stages that should only appear once */
const DEDUPE_STAGES = new Set(['queued', 'processing', 'plan_edited']);

/* ── Skeleton loading UI ─────────────────────────────────────── */
// Uses the `shimmer` @keyframes already defined in dashboard.css
const SK = {
  base: {
    background: 'linear-gradient(90deg, #F5F0EA 25%, #E8E2DC 50%, #F5F0EA 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.4s ease-in-out infinite',
    borderRadius: 6,
    flexShrink: 0,
  },
};
const Sk = ({ w, h = 14, r = 6, style = {} }) => (
  <div style={{ ...SK.base, width: w, height: h, borderRadius: r, ...style }} />
);

function LifecycleSkeleton({ isBatch = false }) {
  const tableRowBg = (i) => ({ background: i % 2 === 0 ? '#fff' : '#FDFCFB' });
  return (
    <div style={{ maxWidth: 860 }}>
      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <Sk w={isBatch ? 160 : 200} h={20} style={{ marginBottom: 8 }} />
          <Sk w={110} h={13} />
        </div>
        <Sk w={76} h={24} r={999} />
      </div>

      {/* ── Summary table ── */}
      <div style={{ marginBottom: 28 }}>
        <Sk w={70} h={11} style={{ marginBottom: 10 }} />
        <div style={{ border: '1px solid #EDE8E1', borderRadius: 8, overflow: 'hidden' }}>
          {/* th row */}
          <div style={{ background: '#FAFAF9', padding: '9px 14px', borderBottom: '1px solid #EDE8E1', display: 'flex', gap: 40 }}>
            <Sk w={55} h={11} /><Sk w={110} h={11} /><Sk w={90} h={11} />
          </div>
          {[0, 1, 2, ...(isBatch ? [3] : [])].map(i => (
            <div key={i} style={{ ...tableRowBg(i), padding: '11px 14px', borderBottom: '1px solid #F5F0EA', display: 'flex', gap: 40, alignItems: 'center' }}>
              <Sk w={80} h={13} /><Sk w={120} h={13} /><Sk w={95} h={13} />
            </div>
          ))}
        </div>
      </div>

      {/* ── Files in batch (batch only) ── */}
      {isBatch && (
        <div style={{ marginBottom: 28 }}>
          <Sk w={90} h={11} style={{ marginBottom: 10 }} />
          <div style={{ border: '1px solid #EDE8E1', borderRadius: 8, overflow: 'hidden' }}>
            <div style={{ background: '#FAFAF9', padding: '9px 14px', borderBottom: '1px solid #EDE8E1', display: 'flex', gap: 40 }}>
              <Sk w={90} h={11} /><Sk w={70} h={11} /><Sk w={65} h={11} />
            </div>
            {[0, 1, 2].map(i => (
              <div key={i} style={{ ...tableRowBg(i), padding: '11px 14px', borderBottom: '1px solid #F5F0EA', display: 'flex', gap: 40, alignItems: 'center' }}>
                <Sk w={130} h={13} /><Sk w={60} h={20} r={999} /><Sk w={60} h={20} r={999} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Timeline table ── */}
      <div>
        <Sk w={120} h={11} style={{ marginBottom: 10 }} />
        <div style={{ border: '1px solid #EDE8E1', borderRadius: 8, overflow: 'hidden' }}>
          <div style={{ background: '#FAFAF9', padding: '9px 14px', borderBottom: '1px solid #EDE8E1', display: 'flex', gap: 24 }}>
            <Sk w={60} h={11} /><Sk w={90} h={11} /><Sk w={65} h={11} /><Sk w={85} h={11} />
          </div>
          {[0, 1, 2, 3, 4].map(i => (
            <div key={i} style={{ ...tableRowBg(i), padding: '11px 14px', borderBottom: '1px solid #F5F0EA', display: 'flex', gap: 24, alignItems: 'center' }}>
              <Sk w={80} h={22} r={999} />
              <Sk w={100} h={13} />
              <Sk w={72} h={13} />
              <Sk w={88} h={13} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── LifecyclePanel — the right-panel detail view ──────────── */
export function LifecyclePanel({ drawingId }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    if (!drawingId) return;
    setLoading(true); setData(null); setError(null);
    API.getLifecycle(drawingId)
      .then(d  => { setData(d);           setLoading(false); })
      .catch(e => { setError(e?.error || 'Failed to load lifecycle'); setLoading(false); });
  }, [drawingId]);

  if (loading) return <div style={s.main}><LifecycleSkeleton /></div>;
  if (error)   return <div style={{ ...s.emptyState, color: '#991B1B' }}>{error}</div>;
  if (!data)   return null;

  /* Deduplicate queued/processing — only keep first occurrence */
  const seen = new Set();
  const deduped = data.events.filter(ev => {
    if (!DEDUPE_STAGES.has(ev.stage)) return true;
    if (seen.has(ev.stage)) return false;
    seen.add(ev.stage); return true;
  });

  const hasReview = data.reviewed_by || data.reviewed_at;

  return (
    <div style={s.panel}>
      {/* Header */}
      <div style={s.panelHeader}>
        <div>
          <div style={s.panelTitle}>{data.display_name || data.file_name}</div>
          <div style={s.panelSub}>Drawing ID #{data.drawing_id}</div>
        </div>
        <StatusPill status={data.status} />
      </div>

      {/* ── Summary table ── */}
      <div style={s.section}>
        <div style={s.sectionTitle}>Summary</div>
        <table style={s.summaryTable}>
          <thead>
            <tr>
              <th style={s.summaryTh}>Detail</th>
              <th style={s.summaryTh}>Name / Value</th>
              <th style={s.summaryTh}>Timestamp</th>
            </tr>
          </thead>
          <tbody>
            {/* Created by */}
            <tr>
              <td style={s.summaryTdLabel}>Created by</td>
              <td style={s.summaryTd}>{data.created_by || '—'}</td>
              <td style={s.summaryTdDate}>{fmtLifecycleDate(data.created_at)}</td>
            </tr>

            {/* Scan duration */}
            <tr>
              <td style={s.summaryTdLabel}>Scan Duration</td>
              <td style={s.summaryTd}>
                {data.processing_duration_seconds != null
                  ? fmtDuration(data.processing_duration_seconds)
                  : '—'}
              </td>
              <td style={s.summaryTdDate}>—</td>
            </tr>

            {/* Inspection plan edited */}
            <tr>
              <td style={s.summaryTdLabel}>Inspection Plan</td>
              <td style={s.summaryTd}>
                {data.plan_edited_by
                  ? <span style={{ color: '#6D28D9', fontWeight: 600 }}>Edited by {data.plan_edited_by}</span>
                  : <span style={{ color: '#9C9490' }}>Not Edited</span>}
              </td>
              <td style={s.summaryTdDate}>{fmtLifecycleDate(data.plan_edited_at)}</td>
            </tr>

            {/* Reviewed by */}
            {hasReview && (
              <tr>
                <td style={s.summaryTdLabel}>
                  {data.review_outcome === 'approved'      ? 'Approved by' :
                   data.review_outcome === 'self_approved' ? 'Self-approved by' :
                   data.review_outcome === 'rejected'      ? 'Rejected by' : 'Reviewed by'}
                </td>
                <td style={s.summaryTd}>
                  <div>{data.reviewed_by || '—'}</div>
                  {data.reviewer_comment && (
                    <div style={s.commentQuote}>"{data.reviewer_comment}"</div>
                  )}
                </td>
                <td style={s.summaryTdDate}>{fmtLifecycleDate(data.reviewed_at)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── Full event timeline ── */}
      <div style={s.section}>
        <div style={s.sectionTitle}>Full Event Timeline</div>
        {deduped.length === 0 ? (
          <p style={{ color: '#9C9490', fontSize: 13 }}>No events recorded yet.</p>
        ) : (
          <div style={s.timelineWrap}>
            <table style={s.tlTable}>
              <thead>
                <tr>
                  <th style={s.tlTh}>Stage</th>
                  <th style={s.tlTh}>Details</th>
                  <th style={s.tlTh}>Actor</th>
                  <th style={s.tlTh}>Timestamp</th>
                </tr>
              </thead>
              <tbody>
                {deduped.map((ev, i) => {
                  const cs = stageStyle(ev.stage);
                  const hasComment = ev.metadata?.comments;
                  const hasError   = ev.metadata?.error;
                  return (
                    <tr key={ev.id || i} style={i % 2 === 0 ? s.tlRowEven : s.tlRowOdd}>
                      <td style={s.tlTd}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: cs.dot, flexShrink: 0 }} />
                          <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: cs.bg, color: cs.color }}>
                            {ev.label}
                          </span>
                        </span>
                      </td>
                      <td style={{ ...s.tlTd, maxWidth: 220 }}>
                        {ev.stage === 'processing' && data.processing_duration_seconds != null && (
                          <div style={{ fontSize: 11, color: '#C2410C', marginBottom: hasComment || hasError ? 4 : 0 }}>
                            Duration: {fmtDuration(data.processing_duration_seconds)}
                          </div>
                        )}
                        {(ev.stage === 'shared' || ev.stage === 're_shared') && ev.metadata?.reviewer_emails?.length > 0 && (
                          <div style={{ fontSize: 11, color: '#1E40AF', marginBottom: hasComment || hasError ? 4 : 0 }}>
                            Sent to: {ev.metadata.reviewer_emails.join(', ')}
                          </div>
                        )}
                        {hasComment && (
                          <div style={s.commentQuote}>"{hasComment}"</div>
                        )}
                        {hasError && (
                          <div style={{ fontSize: 11, color: '#991B1B', fontStyle: 'italic' }}>{hasError}</div>
                        )}
                        {ev.stage !== 'processing' && !(ev.stage === 'shared' || ev.stage === 're_shared') && !hasComment && !hasError && (
                          <span style={{ color: '#C4BDB8', fontSize: 12 }}>—</span>
                        )}
                        {ev.stage === 'processing' && data.processing_duration_seconds == null && !hasComment && !hasError && (
                          <span style={{ color: '#C4BDB8', fontSize: 12 }}>—</span>
                        )}
                        {(ev.stage === 'shared' || ev.stage === 're_shared') && !ev.metadata?.reviewer_emails?.length && !hasComment && !hasError && (
                          <span style={{ color: '#C4BDB8', fontSize: 12 }}>—</span>
                        )}
                      </td>
                      <td style={s.tlTd}>
                        <span style={{ fontSize: 13, color: ev.actor_role === 'system' ? '#9C9490' : '#2C2825' }}>
                          {ev.actor_name || '—'}
                        </span>
                        {ev.actor_role && ev.actor_role !== 'system' && (
                          <span style={{ fontSize: 11, color: '#9C9490', marginLeft: 4 }}>({ev.actor_role})</span>
                        )}
                      </td>
                      <td style={{ ...s.tlTd, color: '#6B6360', fontSize: 12, whiteSpace: 'nowrap' }}>
                        {fmtLifecycleDate(ev.created_at)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── BatchLifecyclePanel — right-panel batch overview ────────── */
export function BatchLifecyclePanel({ batchId }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    if (!batchId) return;
    setLoading(true); setData(null); setError(null);
    API.getLifecycleBatch(batchId)
      .then(d  => { setData(d);           setLoading(false); })
      .catch(e => { setError(e?.error || 'Failed to load batch lifecycle'); setLoading(false); });
  }, [batchId]);

  if (loading) return <div style={s.main}><LifecycleSkeleton isBatch /></div>;
  if (error)   return <div style={{ ...s.emptyState, color: '#991B1B' }}>{error}</div>;
  if (!data)   return null;

  /* Deduplicate batch events — already deduplicated by backend but apply locally too */
  const seen = new Set();
  const deduped = (data.events || []).filter(ev => {
    if (!DEDUPE_STAGES.has(ev.stage)) return true;
    if (seen.has(ev.stage)) return false;
    seen.add(ev.stage); return true;
  });

  const approvalStyle =
    data.overall_approval === 'approved'      ? { bg: '#ECFDF5', color: '#065F46' } :
    data.overall_approval === 'rejected'      ? { bg: '#FEF2F2', color: '#991B1B' } :
    data.overall_approval === 'pending_review'? { bg: '#FFF7ED', color: '#92400E' } :
                                                { bg: '#F3F4F6', color: '#374151' };

  return (
    <div style={s.panel}>
      {/* Header */}
      <div style={s.panelHeader}>
        <div>
          <div style={s.panelTitle}>{data.batch_display || `Batch (${data.file_count} files)`}</div>
          <div style={s.panelSub}>{data.file_count} file{data.file_count !== 1 ? 's' : ''} in this batch</div>
        </div>
        <span style={{ padding: '4px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700, background: approvalStyle.bg, color: approvalStyle.color }}>
          {(data.overall_approval || 'pending').replace('_', ' ')}
        </span>
      </div>

      {/* ── Batch Summary ── */}
      <div style={s.section}>
        <div style={s.sectionTitle}>Summary</div>
        <table style={s.summaryTable}>
          <thead>
            <tr>
              <th style={s.summaryTh}>Detail</th>
              <th style={s.summaryTh}>Value</th>
              <th style={s.summaryTh}>Timestamp</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={s.summaryTdLabel}>Created by</td>
              <td style={s.summaryTd}>{data.created_by || '—'}</td>
              <td style={s.summaryTdDate}>{fmtLifecycleDate(data.created_at)}</td>
            </tr>
            <tr>
              <td style={s.summaryTdLabel}>Scan Duration</td>
              <td style={s.summaryTd}>
                {data.processing_duration_seconds != null ? fmtDuration(data.processing_duration_seconds) : '—'}
              </td>
              <td style={s.summaryTdDate}>—</td>
            </tr>
            <tr>
              <td style={s.summaryTdLabel}>Files in Batch</td>
              <td style={s.summaryTd}>{data.file_count}</td>
              <td style={s.summaryTdDate}>—</td>
            </tr>
            <tr>
              <td style={s.summaryTdLabel}>Overall Approval</td>
              <td style={s.summaryTd}>
                <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: approvalStyle.bg, color: approvalStyle.color }}>
                  {(data.overall_approval || 'pending').replace('_', ' ')}
                </span>
              </td>
              <td style={s.summaryTdDate}>—</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ── Per-file status ── */}
      {data.files && data.files.length > 0 && (
        <div style={s.section}>
          <div style={s.sectionTitle}>Files in Batch</div>
          <div style={s.timelineWrap}>
            <table style={s.tlTable}>
              <thead>
                <tr>
                  <th style={s.tlTh}>Part Number</th>
                  <th style={s.tlTh}>Scan Status</th>
                  <th style={s.tlTh}>Approval</th>
                </tr>
              </thead>
              <tbody>
                {data.files.map((f, i) => (
                  <tr key={f.id} style={i % 2 === 0 ? s.tlRowEven : s.tlRowOdd}>
                    <td style={s.tlTd}>{f.display_name || f.file_name}</td>
                    <td style={s.tlTd}><StatusPill status={f.status} /></td>
                    <td style={s.tlTd}><ApprovalPill status={f.approval_status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Batch Event Timeline ── */}
      <div style={s.section}>
        <div style={s.sectionTitle}>Batch Event Timeline</div>
        {deduped.length === 0 ? (
          <p style={{ color: '#9C9490', fontSize: 13 }}>No events recorded yet.</p>
        ) : (
          <div style={s.timelineWrap}>
            <table style={s.tlTable}>
              <thead>
                <tr>
                  <th style={s.tlTh}>Stage</th>
                  <th style={s.tlTh}>Details</th>
                  <th style={s.tlTh}>Actor</th>
                  <th style={s.tlTh}>Timestamp</th>
                </tr>
              </thead>
              <tbody>
                {deduped.map((ev, i) => {
                  const cs = stageStyle(ev.stage);
                  const hasComment = ev.metadata?.comments;
                  const hasError   = ev.metadata?.error;
                  const isShared   = ev.stage === 'shared' || ev.stage === 're_shared';
                  return (
                    <tr key={ev.id || i} style={i % 2 === 0 ? s.tlRowEven : s.tlRowOdd}>
                      <td style={s.tlTd}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: cs.dot, flexShrink: 0 }} />
                          <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: cs.bg, color: cs.color }}>
                            {ev.label}
                          </span>
                        </span>
                      </td>
                      <td style={{ ...s.tlTd, maxWidth: 220 }}>
                        {ev.stage === 'processing' && data.processing_duration_seconds != null && (
                          <div style={{ fontSize: 11, color: '#C2410C', marginBottom: hasComment || hasError ? 4 : 0 }}>
                            Duration: {fmtDuration(data.processing_duration_seconds)}
                          </div>
                        )}
                        {isShared && ev.metadata?.reviewer_emails?.length > 0 && (
                          <div style={{ fontSize: 11, color: '#1E40AF', marginBottom: hasComment || hasError ? 4 : 0 }}>
                            Sent to: {ev.metadata.reviewer_emails.join(', ')}
                          </div>
                        )}
                        {hasComment && (
                          <div style={s.commentQuote}>"{hasComment}"</div>
                        )}
                        {hasError && (
                          <div style={{ fontSize: 11, color: '#991B1B', fontStyle: 'italic' }}>{hasError}</div>
                        )}
                        {ev.stage !== 'processing' && !isShared && !hasComment && !hasError && (
                          <span style={{ color: '#C4BDB8', fontSize: 12 }}>—</span>
                        )}
                        {ev.stage === 'processing' && data.processing_duration_seconds == null && !hasComment && !hasError && (
                          <span style={{ color: '#C4BDB8', fontSize: 12 }}>—</span>
                        )}
                        {isShared && !ev.metadata?.reviewer_emails?.length && !hasComment && !hasError && (
                          <span style={{ color: '#C4BDB8', fontSize: 12 }}>—</span>
                        )}
                      </td>
                      <td style={s.tlTd}>
                        <span style={{ fontSize: 13, color: ev.actor_role === 'system' ? '#9C9490' : '#2C2825' }}>
                          {ev.actor_name || '—'}
                        </span>
                        {ev.actor_role && ev.actor_role !== 'system' && (
                          <span style={{ fontSize: 11, color: '#9C9490', marginLeft: 4 }}>({ev.actor_role})</span>
                        )}
                      </td>
                      <td style={{ ...s.tlTd, color: '#6B6360', fontSize: 12, whiteSpace: 'nowrap' }}>
                        {fmtLifecycleDate(ev.created_at)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Main page ────────────────────────────────────────────────  */
export default function LifecyclePage() {
  const [history,         setHistory]         = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [selectedId,      setSelectedId]      = useState(null);
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  const [search,          setSearch]          = useState('');
  const [expandedBatches, setExpandedBatches] = useState(new Set());

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await API.getHistory(0, 500);
      setHistory(Array.isArray(data) ? data : []);
    } catch (_) {
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleBatch = batchId =>
    setExpandedBatches(prev => {
      const next = new Set(prev);
      next.has(batchId) ? next.delete(batchId) : next.add(batchId);
      return next;
    });

  /* Apply search filter — matches batch label OR any child file name */
  const matchesSearch = (item) => {
    const q = search.toLowerCase();
    if (!q) return true;
    if (item.is_batch) {
      return (item.files || []).some(f =>
        (f.display_name || f.file_name || '').toLowerCase().includes(q)
      );
    }
    return (item.display_name || item.file_name || '').toLowerCase().includes(q);
  };
  const filtered = history.filter(matchesSearch);

  return (
    <div style={s.root}>
      {/* ── Left: file/batch list ── */}
      <aside style={s.sidebar}>
        <div style={s.sidebarHeader}>
          <div style={s.sidebarTitle}>Lifecycle Tracker</div>
          <input
            style={s.searchInput}
            placeholder="Search part number…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        <div style={s.fileList}>
          {loading && <div style={s.listEmpty}>Loading…</div>}
          {!loading && filtered.length === 0 && (
            <div style={s.listEmpty}>No scans found.</div>
          )}

          {filtered.map(item => {
            if (item.is_batch) {
              const isOpen      = expandedBatches.has(item.batch_id);
              const batchActive = selectedBatchId === item.batch_id && !selectedId;
              const children    = item.files || [];
              return (
                <div key={`batch-${item.batch_id}`}>
                  {/* Batch header row — arrow toggles expand, label selects batch lifecycle */}
                  <div style={{ ...s.fileItem, ...s.batchHeader, ...(batchActive ? s.fileItemActive : {}), display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}>
                      <button
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px 0 0', color: '#B45309', fontSize: 12, flexShrink: 0 }}
                        onClick={e => { e.stopPropagation(); toggleBatch(item.batch_id); }}
                        title={isOpen ? 'Collapse' : 'Expand'}
                      >
                        {isOpen ? '▼' : '▶'}
                      </button>
                      <button
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left', flex: 1, minWidth: 0 }}
                        onClick={() => { setSelectedBatchId(item.batch_id); setSelectedId(null); }}
                        title="View batch lifecycle"
                      >
                        <div style={s.fileItemName}>{item.display_name || item.file_name || `Batch (${children.length} files)`}</div>
                      </button>
                    </div>
                    <div style={s.fileItemMeta}>
                      <StatusPill status={item.status} />
                      <ApprovalPill status={item.approval_status} />
                    </div>
                  </div>

                  {/* Child file buttons */}
                  {isOpen && children.map(f => {
                    const active = selectedId === f.id;
                    return (
                      <button
                        key={f.id}
                        style={{ ...s.fileItem, ...s.childItem, ...(active ? s.fileItemActive : {}) }}
                        onClick={() => { setSelectedId(f.id); setSelectedBatchId(null); }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          <span style={{ color: '#B45309', fontSize: 11 }}>└</span>
                          <div style={{ ...s.fileItemName, fontSize: 12 }}>{f.display_name || f.file_name}</div>
                        </div>
                        <div style={s.fileItemMeta}>
                          <StatusPill status={f.status} />
                          <ApprovalPill status={f.approval_status} />
                        </div>
                      </button>
                    );
                  })}
                </div>
              );
            }

            // Solo file
            const active = selectedId === item.id;
            return (
              <button
                key={item.id}
                style={{ ...s.fileItem, ...(active ? s.fileItemActive : {}) }}
                onClick={() => { setSelectedId(item.id); setSelectedBatchId(null); }}
              >
                <div style={s.fileItemName}>{item.display_name || item.file_name}</div>
                <div style={s.fileItemMeta}>
                  <StatusPill status={item.status} />
                  <ApprovalPill status={item.approval_status} />
                </div>
              </button>
            );
          })}
        </div>
      </aside>

      {/* ── Right: lifecycle panel ── */}
      <main style={s.main}>
        {!selectedId && !selectedBatchId ? (
          <div style={s.emptyState}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#D1CBC4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
            </svg>
            <div style={{ marginTop: 12, color: '#9C9490', fontSize: 14 }}>
              Select a part number or batch on the left to view its lifecycle.
            </div>
          </div>
        ) : selectedBatchId ? (
          <BatchLifecyclePanel batchId={selectedBatchId} />
        ) : (
          <LifecyclePanel drawingId={selectedId} />
        )}
      </main>
    </div>
  );
}

/* ── Styles ─────────────────────────────────────────────────── */
const s = {
  root: { display: 'flex', height: '100%', overflow: 'hidden', background: '#FAFAF9', fontFamily: 'inherit' },

  sidebar:      { width: 280, flexShrink: 0, borderRight: '1px solid #EDE8E1', display: 'flex', flexDirection: 'column', background: '#fff', overflowY: 'auto' },
  sidebarHeader:{ padding: '20px 16px 12px', borderBottom: '1px solid #EDE8E1', position: 'sticky', top: 0, background: '#fff', zIndex: 1 },
  sidebarTitle: { fontSize: 15, fontWeight: 700, color: '#2C2825', marginBottom: 10 },
  searchInput:  { width: '100%', boxSizing: 'border-box', padding: '7px 10px', borderRadius: 8, border: '1px solid #EDE8E1', fontSize: 13, outline: 'none', background: '#FAFAF9' },
  fileList:     { flex: 1, overflowY: 'auto', padding: '8px 0' },
  listEmpty:    { padding: '16px', color: '#9C9490', fontSize: 13, textAlign: 'center' },

  fileItem: {
    display: 'block', width: '100%', textAlign: 'left',
    padding: '10px 16px', background: 'none', border: 'none',
    borderBottom: '1px solid #F5F0EA', cursor: 'pointer', transition: 'background 0.12s',
  },
  batchHeader:   { background: '#FFFBF5', borderLeft: '3px solid transparent' },
  childItem:     { paddingLeft: 28, background: '#FDFAF6' },
  fileItemActive:{ background: '#FFF7ED !important', borderLeft: '3px solid #D97706' },
  fileItemName:  { fontSize: 13, fontWeight: 600, color: '#2C2825', marginBottom: 4 },
  fileItemMeta:  { display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' },

  main:       { flex: 1, overflowY: 'auto', padding: '24px 28px' },
  emptyState: { height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8 },
  spinner:    { width: 24, height: 24, borderRadius: '50%', border: '3px solid #EDE8E1', borderTopColor: '#D97706', animation: 'spin 0.7s linear infinite' },

  panel:      { maxWidth: 860 },
  panelHeader:{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 },
  panelTitle: { fontSize: 20, fontWeight: 700, color: '#2C2825' },
  panelSub:   { fontSize: 12, color: '#9C9490', marginTop: 2 },

  section:      { marginBottom: 28 },
  sectionTitle: { fontSize: 12, fontWeight: 700, color: '#9C9490', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 10 },

  summaryTable:   { width: '100%', borderCollapse: 'collapse', border: '1px solid #EDE8E1', borderRadius: 8, overflow: 'hidden', fontSize: 13 },
  summaryTh:      { padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#9C9490', background: '#FAFAF9', borderBottom: '1px solid #EDE8E1', letterSpacing: '0.05em', textTransform: 'uppercase' },
  summaryTdLabel: { padding: '10px 14px', fontWeight: 600, color: '#2C2825', fontSize: 13, borderBottom: '1px solid #F5F0EA', background: '#FDFCFB', whiteSpace: 'nowrap' },
  summaryTd:      { padding: '10px 14px', color: '#2C2825', fontSize: 13, borderBottom: '1px solid #F5F0EA' },
  summaryTdDate:  { padding: '10px 14px', color: '#6B6360', fontSize: 12, borderBottom: '1px solid #F5F0EA', whiteSpace: 'nowrap' },

  timelineWrap: { overflowX: 'auto', borderRadius: 8, border: '1px solid #EDE8E1' },
  tlTable:      { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  tlTh:         { padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#9C9490', background: '#FAFAF9', borderBottom: '1px solid #EDE8E1', letterSpacing: '0.05em', textTransform: 'uppercase', whiteSpace: 'nowrap' },
  tlTd:         { padding: '10px 14px', verticalAlign: 'middle' },
  tlRowEven:    { background: '#fff' },
  tlRowOdd:     { background: '#FDFCFB' },

  commentQuote: { marginTop: 4, fontSize: 12, color: '#6B4E3D', fontStyle: 'italic', background: '#FFF8F0', borderLeft: '3px solid #F97316', paddingLeft: 8, paddingTop: 3, paddingBottom: 3, borderRadius: 2 },
};
